import React, { useCallback, useState, forwardRef } from 'react';
import { useDropzone } from 'react-dropzone';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
} from '@mui/material';
import {
  CloudUpload,
  Close as CloseIcon,
  CheckCircle,
  CameraAlt,
  Refresh,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

const EnhancedImageUpload = forwardRef(({
  onUpload,
  maxSize = 5 * 1024 * 1024,
  title = "Upload Your Question",
  subtitle = "Drag and drop your image here or click to select",
  supportedFormats = "Supported formats: JPEG, PNG, GIF",
  enableCropping = true,
}, ref) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  
  // Cropping states
  const [showCropDialog, setShowCropDialog] = useState(false);
  const [crop, setCrop] = useState({ unit: '%', width: 100, aspect: undefined });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [originalImage, setOriginalImage] = useState(null);

  const onDrop = useCallback((acceptedFiles) => {
    setError(null);
    setUploadComplete(false);
    setRetryCount(0);

    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      if (file.size > maxSize) {
        setError(`File size must be less than ${maxSize / (1024 * 1024)}MB`);
        return;
      }

      if (file.type.startsWith('image/')) {
        setFile(file);
        const reader = new FileReader();
        reader.onload = (e) => {
          const result = e.target?.result;
          setOriginalImage(result);
          if (enableCropping) {
            setShowCropDialog(true);
          } else {
            setPreview(result);
          }
        };
        reader.readAsDataURL(file);
      } else {
        setError('Please upload an image file.');
      }
    }
  }, [maxSize, enableCropping]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif']
    },
    maxFiles: 1,
    maxSize
  });

  const getCroppedImg = async (imageSrc, crop) => {
    return new Promise((resolve) => {
      const image = new Image();
      image.src = imageSrc;
      image.onload = () => {
        const canvas = document.createElement('canvas');
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        canvas.width = crop.width;
        canvas.height = crop.height;
        const ctx = canvas.getContext('2d');

        ctx.drawImage(
          image,
          crop.x * scaleX,
          crop.y * scaleY,
          crop.width * scaleX,
          crop.height * scaleY,
          0,
          0,
          crop.width,
          crop.height
        );

        canvas.toBlob((blob) => {
          resolve(blob);
        }, 'image/jpeg', 1);
      };
    });
  };

  const handleSaveCrop = async () => {
    if (originalImage && completedCrop?.width && completedCrop?.height) {
      try {
        setUploading(true);
        const croppedImage = await getCroppedImg(originalImage, completedCrop);
        const imageUrl = URL.createObjectURL(croppedImage);
        setPreview(imageUrl);
        setFile(new File([croppedImage], 'cropped_image.jpg', { type: 'image/jpeg' }));
      } catch (error) {
        console.error('Error processing image:', error);
        setError('Failed to process the cropped image.');
      } finally {
        setUploading(false);
        setShowCropDialog(false);
      }
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setError(null);

    try {
      await onUpload(file);
      setUploadComplete(true);
      setRetryCount(0);
    } catch (err) {
      console.error('Upload error:', err);
      if (err.type === 'TIMEOUT_ERROR' && retryCount < 3) {
        setError(`Analysis taking longer than expected. Retrying... (${retryCount + 1}/3)`);
        setRetryCount(prev => prev + 1);
        setTimeout(handleUpload, 2000);
      } else {
        setError(err.message || 'Failed to analyze image. Please try again.');
      }
    } finally {
      if (!error) {
        setUploading(false);
      }
    }
  };

  const handleRemove = () => {
    setFile(null);
    setPreview(null);
    setError(null);
    setUploadComplete(false);
    setRetryCount(0);
    setOriginalImage(null);
    setCompletedCrop(null);
  };

  const handleRetry = () => {
    setError(null);
    setUploading(false);
    setRetryCount(0);
    handleUpload();
  };

  return (
    <>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          bgcolor: 'background.paper',
          borderRadius: 3,
          background: 'linear-gradient(145deg, #f7f9fc 0%, #eef1f5 100%)',
        }}
      >
        <Typography variant="h5" align="center" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
          {title}
        </Typography>

        <Box
          {...getRootProps()}
          sx={{
            mt: 3,
            p: 4,
            border: '2px dashed',
            borderColor: isDragActive ? 'primary.main' : 'divider',
            borderRadius: 2,
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            bgcolor: isDragActive ? 'action.hover' : 'background.default',
            '&:hover': {
              borderColor: 'primary.main',
              transform: 'translateY(-2px)',
              boxShadow: 2,
            },
          }}
        >
          <input {...getInputProps()} />
          <AnimatePresence mode="wait">
            {preview ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
              >
                <Box sx={{ position: 'relative' }}>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove();
                    }}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      bgcolor: 'error.main',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'error.dark',
                      },
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                  <Box
                    component="img"
                    src={preview}
                    alt="Preview"
                    sx={{
                      width: '100%',
                      maxHeight: 300,
                      objectFit: 'contain',
                      borderRadius: 2,
                    }}
                  />
                </Box>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bgcolor: 'primary.light',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                  }}
                >
                  <CameraAlt sx={{ fontSize: 40, color: 'primary.main' }} />
                </Box>
                <Typography variant="h6" align="center" gutterBottom>
                  {subtitle}
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center">
                  {supportedFormats}
                </Typography>
              </motion.div>
            )}
          </AnimatePresence>
        </Box>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Alert 
              severity="error" 
              sx={{ mt: 2 }}
              action={
                error.includes('taking longer') && (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={handleRetry}
                    startIcon={<Refresh />}
                  >
                    Retry Now
                  </Button>
                )
              }
            >
              {error}
            </Alert>
          </motion.div>
        )}

        {file && !error && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleUpload}
                disabled={uploading}
                startIcon={uploading ? <CircularProgress size={20} /> : <CloudUpload />}
                sx={{
                  minWidth: 200,
                  py: 1.5,
                  background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #1976D2 30%, #00B4D8 90%)',
                  },
                }}
              >
                {uploading ? 'Processing...' : 'Analyze Question'}
              </Button>
            </Box>
          </motion.div>
        )}
      </Paper>

      {/* Crop Dialog */}
      <Dialog
        open={showCropDialog}
        onClose={() => setShowCropDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography>Crop Image</Typography>
            <IconButton onClick={() => setShowCropDialog(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {originalImage && (
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              onComplete={setCompletedCrop}
              aspect={undefined}
            >
              <img
                src={originalImage}
                alt="Crop"
                style={{
                  maxWidth: '100%',
                  maxHeight: 'calc(100vh - 300px)',
                }}
              />
            </ReactCrop>
          )}
          {uploading && (
            <Box sx={{ width: '100%', mt: 2 }}>
              <LinearProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCropDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSaveCrop}
            variant="contained"
            disabled={!completedCrop?.width || !completedCrop?.height || uploading}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default EnhancedImageUpload; 